'use client';

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { Campaign, ApiKeys } from '@/types/campaign';
import { saveCampaign, loadCampaign, createNewCampaign, saveApiKeys, loadApiKeys, saveDraft, loadDraft } from './store';

interface CampaignContextType {
  campaign: Campaign;
  apiKeys: ApiKeys;
  updateCampaign: (updates: Partial<Campaign>) => void;
  updateApiKeys: (keys: ApiKeys) => void;
  resetCampaign: () => void;
  goToStep: (step: number) => void;
  loadDraftCampaign: (id: string) => void;
}

const CampaignContext = createContext<CampaignContextType | null>(null);

const defaultApiKeys: ApiKeys = {
  falKey: '5cc657fe-32cd-43fd-97e6-2b0c78b5417d:e6878f684a891cd4bbcd94b1bc92209e',
  heygenApiKey: 'sk_V2_hgu_kN6iLO97r4u_UAQ3AJnWJA4FPNUvPjNR9FS4xZJ4Of6V',
  metaAccessToken: 'EAAYCZCo5GeRUBRGRWTRT9REZCzlTfeZBVNyj8baBbGBKhnGcbFKl3Aqch7901f82HNk2iwWaR8kFHl2hJft7NxPISaJJ5VLZBShYl6MpW4n2MC2gE4Mx5CAqMdq49a3rORQ4zPu6N8JoXe9UDAW7KwrMucKNmthHMzzZC9uStZCcF6VAkc3FcI4FUFHawZB2A8YlxEVDXZBj',
  metaAdAccountId: 'act_1033292858105951',
  metaPageId: '683950944804447',
  metaPixelId: '1212710254265165',
  anthropicApiKey: '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',
  openaiApiKey: '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',
  clickbankApiKey: 'API-AHJDKPSHID8TQDKYN8NDLA8E5B4M1CFV9R8E',
};

export function CampaignProvider({ children }: { children: React.ReactNode }) {
  const [campaign, setCampaign] = useState<Campaign>(createNewCampaign());
  const [apiKeys, setApiKeys] = useState<ApiKeys>(defaultApiKeys);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const saved = loadCampaign();
    if (saved) setCampaign(saved);
    const savedKeys = loadApiKeys();
    if (savedKeys) {
      // Version-based migration: bump this number when the default OpenRouter key (field still named anthropicApiKey for compatibility) changes.
      // On mismatch, overwrite the cached LLM key with the current code default.
      const CURRENT_KEYS_VERSION = 5;
      const savedVersion = typeof window !== 'undefined'
        ? Number(localStorage.getItem('adforge_keys_version') || '0')
        : 0;
      if (savedVersion < CURRENT_KEYS_VERSION || !savedKeys.anthropicApiKey) {
        const migrated = { ...savedKeys, anthropicApiKey: defaultApiKeys.anthropicApiKey };
        setApiKeys(migrated);
        saveApiKeys(migrated);
        if (typeof window !== 'undefined') {
          localStorage.setItem('adforge_keys_version', String(CURRENT_KEYS_VERSION));
        }
      } else {
        setApiKeys(savedKeys);
      }
    }
    setLoaded(true);
  }, []);

  const updateCampaign = useCallback((updates: Partial<Campaign>) => {
    setCampaign(prev => {
      const updated = { ...prev, ...updates };
      saveCampaign(updated);
      saveDraft(updated);
      return updated;
    });
  }, []);

  const updateApiKeys = useCallback((keys: ApiKeys) => {
    setApiKeys(keys);
    saveApiKeys(keys);
  }, []);

  const resetCampaign = useCallback(() => {
    const fresh = createNewCampaign();
    setCampaign(fresh);
    saveCampaign(fresh);
  }, []);

  const goToStep = useCallback((step: number) => {
    setCampaign(prev => {
      const updated = { ...prev, currentStep: step };
      saveCampaign(updated);
      saveDraft(updated);
      return updated;
    });
  }, []);

  const loadDraftCampaign = useCallback((id: string) => {
    const draft = loadDraft(id);
    if (draft) {
      setCampaign(draft);
      saveCampaign(draft);
    }
  }, []);

  if (!loaded) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="animate-pulse text-gray-400">Loading...</div>
      </div>
    );
  }

  return (
    <CampaignContext.Provider value={{ campaign, apiKeys, updateCampaign, updateApiKeys, resetCampaign, goToStep, loadDraftCampaign }}>
      {children}
    </CampaignContext.Provider>
  );
}

export function useCampaign() {
  const ctx = useContext(CampaignContext);
  if (!ctx) throw new Error('useCampaign must be used within CampaignProvider');
  return ctx;
}
